import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import logger from '../../utils/logger';
import { getToken } from '../../utils/tokenManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function usePerformanceData() {
  const [performanceData, setPerformanceData] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('MONTHLY');
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [missingFields, setMissingFields] = useState(null);
  const [completenessBreakdown, setCompletenessBreakdown] = useState([]);
  const [snapshotFrequency, setSnapshotFrequency] = useState('daily');

  const periods = [
    { value: 'MONTHLY', label: 'Monthly' },
    { value: 'QUARTERLY', label: 'Quarterly' },
    { value: 'ANNUALLY', label: 'Annually' }
  ];

  const frequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'bi_weekly', label: 'Bi-Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'semi_annually', label: 'Semi-Annually' },
    { value: 'annually', label: 'Annually' },
  ];

  const getHeaders = () => {
    const token = getToken();
    return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
  };

  const loadPerformanceData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/portfolio/performance?period=${selectedPeriod}`,
        { headers: getHeaders(), cache: 'no-store' }
      );
      if (!response.ok) throw new Error('Failed to load performance data');
      const data = await response.json();
      setPerformanceData(data.data || []);
    } catch (error) {
      logger.error('Error loading performance data:', error);
      toast.error('Failed to load performance data');
      setPerformanceData([]);
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod]);

  const loadMetrics = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/portfolio/metrics`, {
        headers: getHeaders(), cache: 'no-store',
      });
      if (!response.ok) throw new Error('Failed to load metrics');
      const data = await response.json();
      setMetrics(data.metrics);
    } catch (error) {
      logger.error('Error loading metrics:', error);
    }
  };

  const loadCompleteness = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/portfolio/completeness`, {
        headers: getHeaders(), cache: 'no-store',
      });
      if (response.ok) {
        const data = await response.json();
        setMissingFields(data.missing_summary || {});
        setCompletenessBreakdown(data.breakdown || []);
      }
    } catch (err) {
      logger.error('Performance data error:', err);
    }
  };

  const loadSnapshotFrequency = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/portfolio/snapshot-frequency`, { headers: getHeaders() });
      if (response.ok) {
        const data = await response.json();
        setSnapshotFrequency(data.snapshot_frequency || 'daily');
      }
    } catch (err) { /* silent */ }
  };

  const updateSnapshotFrequency = async (frequency) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/portfolio/snapshot-frequency`, {
        method: 'PUT', headers: getHeaders(),
        body: JSON.stringify({ frequency }),
      });
      if (response.ok) {
        setSnapshotFrequency(frequency);
        toast.success(`Update frequency set to ${frequency}`);
      }
    } catch (err) { toast.error('Failed to update frequency'); }
  };

  const updateSnapshots = async () => {
    setIsUpdating(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/portfolio/update-snapshots`, {
        method: 'POST', headers: getHeaders(),
      });
      const data = await response.json();
      if (data.limited) {
        toast.info(data.message);
      } else if (response.ok) {
        toast.success('Portfolio value updated!');
        await loadPerformanceData();
        await loadMetrics();
      } else {
        throw new Error(data.detail || 'Failed to update');
      }
    } catch (error) {
      logger.error('Error updating snapshots:', error);
      toast.error('Failed to update portfolio value');
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => { loadPerformanceData(); }, [loadPerformanceData]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadMetrics(); loadCompleteness(); loadSnapshotFrequency(); }, []);

  return {
    performanceData, metrics, selectedPeriod, setSelectedPeriod,
    isLoading, isUpdating, missingFields, completenessBreakdown, periods, updateSnapshots,
    snapshotFrequency, frequencyOptions, updateSnapshotFrequency,
  };
}
